import { createContext, useState, useEffect, useRef, useCallback } from 'react';
import type { ListData } from '../../../types';
import usePlayer from './usePlayer';

// Strip Unicode control characters (bidirectional text marks, zero-width chars, etc.)
// These get inserted by iOS when copying URLs and break URL parsing
const stripControlChars = (str: string): string =>
  str.replace(/[\u200B-\u200D\u202A-\u202E\uFEFF]/g, '');

interface SearchContextValue {
  term: string;
  setTerm: (term: string) => void;
  results: ListData[];
  addResult: ListData | string | null;
  isSearching: boolean;
  addingUrl: boolean;
  isSearchActive: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  clearSearch: () => void;
}

const SearchContext = createContext<SearchContextValue>({
  term: '',
  setTerm: () => {},
  results: [],
  addResult: null,
  isSearching: false,
  addingUrl: false,
  isSearchActive: false,
  inputRef: { current: null },
  clearSearch: () => {},
});

const SearchProvider = ({ children }: { children: React.ReactNode }) => {
  const { showLimitModal } = usePlayer();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<ListData[]>([]);
  const [urlToAdd, setUrlToAdd] = useState<string | null>(null);
  const [addingUrl, setAddingUrl] = useState(false);
  const [addResult, setAddResult] = useState<ListData | string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isSearchActive = term.trim().length > 2;

  const clearSearch = useCallback(() => {
    setTerm('');
    setResults([]);
    setUrlToAdd(null);
    setAddingUrl(false);
    setAddResult(null);
  }, []);

  // Search effect
  useEffect(() => {
    const cleanTerm = stripControlChars(term.trim());

    if (cleanTerm.length <= 2) {
      setResults([]);
      setUrlToAdd(null);
      setAddingUrl(false);
      setAddResult(null);
      return;
    }

    const doSearch = async () => {
      setIsSearching(true);
      setUrlToAdd(null);
      setAddingUrl(false);
      setAddResult(null);

      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(cleanTerm)}`,
          { credentials: 'include' }
        );
        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        const data = await response.json();
        setResults(data.results || []);
        setUrlToAdd(data.url);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
        // If it looks like a URL but search failed, show error
        try {
          new URL(cleanTerm);
          setAddResult(`Failed to fetch URL`);
        } catch {
          // Not a URL, just show no results
        }
      } finally {
        setIsSearching(false);
      }
    };

    doSearch();
  }, [term]);

  // Add URL effect (debounced)
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (urlToAdd) {
      setAddingUrl(true);
      setAddResult(null);

      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await fetch('/api/content/add', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sourceUrl: urlToAdd }),
          });
          if (response.status === 403) {
            const data = await response.json().catch(() => ({}));
            if (data?.requiresProPdf) {
              showLimitModal('pdf');
              setAddResult(`PDFs are a Pro beta feature`);
              return;
            }
            if (data?.requiresLogin) {
              showLimitModal('signin');
              setAddResult(`Sign in to keep listening`);
              return;
            }
            if (data?.requiresUpgrade) {
              showLimitModal('upgrade');
              setAddResult(`Upgrade to keep listening`);
              return;
            }
          }
          if (!response.ok) throw new Error(`Server error: ${response.status}`);
          const data = await response.json();
          if (data.error) {
            setAddResult(`Failed: ${data.error}`);
          } else {
            setAddResult(data);
          }
        } catch {
          setAddResult(`Failed to add URL`);
        } finally {
          setAddingUrl(false);
        }
      }, 500);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [urlToAdd, showLimitModal]);

  // Poll for pending items every 1s
  useEffect(() => {
    const addResultPending =
      addResult &&
      typeof addResult !== 'string' &&
      addResult.status === 'pending';

    const pendingResultIds = results
      .filter((item) => item.status === 'pending')
      .map((item) => item.id);

    const hasPendingItems = addResultPending || pendingResultIds.length > 0;
    if (!hasPendingItems) return;

    const interval = setInterval(async () => {
      if (addResultPending && addResult && typeof addResult !== 'string') {
        try {
          const res = await fetch(`/api/content/${addResult.id}`, {
            credentials: 'include',
          });
          const data = await res.json();
          setAddResult(data);
        } catch {
          // ignore errors
        }
      }

      if (pendingResultIds.length > 0) {
        try {
          const res = await fetch('/api/content/batch', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: pendingResultIds }),
          });
          const updated: ListData[] = await res.json();
          setResults((prev) =>
            prev.map((item) => {
              const updatedItem = updated.find((u) => u.id === item.id);
              return updatedItem || item;
            })
          );
        } catch {
          // ignore errors
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [addResult, results]);

  return (
    <SearchContext.Provider
      value={{
        term,
        setTerm,
        results,
        addResult,
        isSearching,
        addingUrl,
        isSearchActive,
        inputRef,
        clearSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export { SearchContext, SearchProvider };
